import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Input } from './ui/input';

function fmt(dt) { return dt ? new Date(dt).toLocaleString() : '\u2014'; }

export function UnsubscribesPanel() {
  const { authAxios } = useAuth();
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (q) => {
    setLoading(true);
    try {
      const { data } = await authAxios.get('/admin/email-unsubscribes', { params: { email: q || undefined } });
      setRows(data);
    } catch (err) {
      toast({ title: 'Could not load unsubscribes', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setLoading(false); }
  }, [authAxios, toast]);

  useEffect(() => {
    const t = setTimeout(() => load(email), 300);
    return () => clearTimeout(t);
  }, [email, load]);

  const resubscribe = async (addr) => {
    if (!window.confirm(`Only re-subscribe ${addr} if they asked you to. Continue?`)) return;
    try {
      await authAxios.delete('/admin/email-unsubscribes', { params: { email: addr } });
      toast({ title: 'Re-subscribed', description: addr });
      load(email);
    } catch (err) {
      toast({ title: 'Re-subscribe failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    }
  };

  return (
    <div data-testid="admin-unsubscribes-panel" className="bg-white rounded-2xl border border-brand-cream-dark p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-serif text-2xl text-brand-green">Email unsubscribes</h2>
          <p className="text-sm text-brand-green/70 mt-1">
            <span data-testid="admin-unsubscribes-count">{rows.length}</span> address{rows.length === 1 ? '' : 'es'} {email ? 'match your search' : 'opted out of non-essential email'}. Honored instantly.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-green/50" />
            <Input data-testid="admin-unsubscribes-search" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Search by email" className="pl-9 w-60" />
          </div>
          <button onClick={() => load(email)} data-testid="admin-unsubscribes-refresh" className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm border border-brand-cream-dark text-brand-green hover:bg-brand-cream">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>
      {rows.length === 0 && !loading ? (
        <p data-testid="admin-unsubscribes-empty" className="text-sm text-brand-green/60 py-6 text-center">{email ? 'No unsubscribes match that email.' : 'No one has unsubscribed.'}</p>
      ) : (
        <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-brand-green/60">
                {['Email', 'Unsubscribed', 'Method', ''].map((h) => <th key={h} className="py-2 pr-4">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.email} data-testid="admin-unsubscribes-row" className="border-t border-brand-cream-dark">
                  <td className="py-2 pr-4 text-brand-green break-all">{r.email}</td>
                  <td className="py-2 pr-4 text-brand-green/80 whitespace-nowrap">{fmt(r.unsubscribed_at)}</td>
                  <td className="py-2 pr-4 text-brand-green/70">{r.method === 'one-click' ? 'One-click (inbox)' : 'Email link'}</td>
                  <td className="py-2 pr-4 text-right">
                    <button data-testid="admin-unsubscribes-resubscribe-btn" onClick={() => resubscribe(r.email)} className="text-xs underline text-brand-green/70 hover:text-brand-green">Re-subscribe</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
